import { useQuery } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { getPallet } from '../../api/pallets'
import { db } from '../../outbox/db'
import { derivedItemCount } from '../../outbox/operations'
import type { ItemSyncState } from '../../components/pallet/ItemRow'
import type { OutboxOp } from '../../outbox/types'

/** The subset of a pallet this screen renders, from either source. */
interface PalletBase {
  itemCount: number
  capacityMax: number
  status: 'open' | 'closed'
  serialNumbers: string[]
}

export interface PalletViewItem {
  serialNumber: string
  state: ItemSyncState
  /** The op holding it, when it is still local — needed to undo or drop it. */
  opId?: string
}

export interface PalletView {
  palletCode: string
  capacityMax: number
  /** Server count plus queued adds minus queued removes. Display only. */
  itemCount: number
  status: 'open' | 'closed'
  items: PalletViewItem[]
  /** True while the screen is rendering from the device rather than a fresh fetch. */
  isStale: boolean
  /** Ops for this pallet that have not been accepted yet, newest last. */
  pending: OutboxOp[]
}

/**
 * One view of a pallet assembled from two sources: the server's last word, and whatever
 * the device has queued since. The operator sees a single list — a serial scanned thirty
 * seconds ago offline sits in it alongside the ones the ERP confirmed, tagged «در صف».
 */
export function usePalletView(palletCode: string): PalletView {
  const query = useQuery({
    queryKey: ['pallets', palletCode],
    queryFn: async ({ signal }) => {
      const pallet = await getPallet(palletCode, signal)

      await db.recentPallets.put({
        palletCode: pallet.palletCode,
        itemCount: pallet.itemCount,
        capacityMax: pallet.capacityMax,
        status: pallet.status,
        serialNumbers: pallet.items.map((item) => item.serialNumber),
        lastActivityAt: pallet.lastActivityAt ?? new Date().toISOString(),
        cachedAt: Date.now(),
      })

      return pallet
    },
    enabled: palletCode !== '',
  })

  const cached = useLiveQuery(() => db.recentPallets.get(palletCode), [palletCode])

  const pending =
    useLiveQuery(
      () =>
        db.outbox
          .where('palletCode')
          .equals(palletCode)
          .filter((op) => op.status !== 'done')
          .sortBy('seq'),
      [palletCode],
      [],
    ) ?? []

  const server = query.data

  // The server's answer when there is one, the device's mirror when there is not. Only
  // the four fields the screen actually renders, so building it needs no clock.
  const base: PalletBase | undefined = server
    ? {
        itemCount: server.itemCount,
        capacityMax: server.capacityMax,
        status: server.status,
        serialNumbers: server.items.map((item) => item.serialNumber),
      }
    : cached

  return {
    palletCode,
    capacityMax: base?.capacityMax ?? 0,
    itemCount: derivedItemCount(base, pending),
    status: base?.status ?? 'open',
    items: buildItems(base, pending),
    isStale: server === undefined,
    pending,
  }
}

/**
 * Server items first, then the device's queued adds appended in scan order. A queued
 * remove takes its serial out of the list immediately: the operator watched it leave the
 * pallet, so the screen must agree with them even before the server does.
 */
function buildItems(base: PalletBase | undefined, pending: OutboxOp[]): PalletViewItem[] {
  const items = new Map<string, PalletViewItem>()

  for (const serialNumber of base?.serialNumbers ?? []) {
    items.set(serialNumber, { serialNumber, state: 'synced' })
  }

  for (const op of pending) {
    for (const serialNumber of op.serialNumbers) {
      if (op.kind === 'remove') {
        items.delete(serialNumber)
        continue
      }

      items.set(serialNumber, {
        serialNumber,
        state: op.status === 'rejected' ? 'failed' : 'queued',
        opId: op.id,
      })
    }
  }

  return [...items.values()]
}
